import { CaretDownOutlined } from "@ant-design/icons";
import { IAnalyticsProcessorConfig } from "@kuika/kuika-cl-model";
import { createRef, PureComponent, ReactNode } from "react";
import { KModal } from "../../../../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../../../../ui-kit/k-modal/k-modal-footer";
import KuikaBadge from "../../../../../ui-kit/kuika-badge/kuika-badge";
import KuikaButton from "../../../../../ui-kit/kuika-button/kuika-button";
import KuikaCheckbox from "../../../../../ui-kit/kuika-checkbox/kuika-checkbox";
import KuikaInput from "../../../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox from "../../../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../../../ui-kit/kuika-svg/kuika-svg";
import "./AnalyticsProcessorZonesModal.scss";

const DEFAULT_EVENT_TYPES = ["object_entered", "object_exited"];
// const DEFAULT_EVENT_TYPES = ["object_entered", "object_exited", "dwell_time", "zone_occupancy"];

export interface IAnalyticsZone {
  id: string;
  name: string;
  x: number;
  y: number;
  w: number;
  h: number;
  triggers: string[];
}

export interface IAnalyticsProcessorZonesModalProps {
  isOpen: boolean;
  config: IAnalyticsProcessorConfig;
  onConfigUpdate: (config: IAnalyticsProcessorConfig) => void;
  onClose: () => void;
}

export interface IAnalyticsProcessorZonesModalState {
  zones: IAnalyticsZone[];
  eventTypes: string[];
  previewSampleUrl: string;
  selectedZoneId: string | null;
  checkedZoneIds: string[];
  collapsedZoneIds: string[];
  showEventTypes: boolean;
  tempZone: { x: number; y: number; w: number; h: number } | null;
  isDrawing: boolean;
  startPoint: { x: number; y: number } | null;
}

export class AnalyticsProcessorZonesModal extends PureComponent<
  IAnalyticsProcessorZonesModalProps,
  IAnalyticsProcessorZonesModalState
> {
  previewRef = createRef<HTMLDivElement>();

  zoneRefs: { [key: string]: HTMLDivElement | null } = {};

  constructor(props: IAnalyticsProcessorZonesModalProps) {
    super(props);
    this.state = {
      zones: [],
      eventTypes: [],
      previewSampleUrl: "",
      selectedZoneId: null,
      checkedZoneIds: [],
      collapsedZoneIds: [],
      showEventTypes: true,
      tempZone: null,
      isDrawing: false,
      startPoint: null
    };
  }

  componentDidUpdate(prevProps: IAnalyticsProcessorZonesModalProps): void {
    if (!prevProps.isOpen && this.props.isOpen) {
      this.handleLoad();
    }
  }

  handleLoad = () => {
    const { config } = this.props;
    // Convert normalized zones (0-1) to pixel zones for display
    const normalizedZones = (config.zones as IAnalyticsZone[]) || [];
    const pixelZones = this.convertZonesToPixels(normalizedZones);

    this.setState({
      zones: pixelZones,
      eventTypes: config.eventTypes || [],
      previewSampleUrl: config.previewSampleUrl || "",
      selectedZoneId: null,
      checkedZoneIds: [],
      collapsedZoneIds: [],
      tempZone: null,
      isDrawing: false,
      startPoint: null
    });
  };

  // Default preview area dimensions (should match CSS)
  static PREVIEW_WIDTH = 600;

  static PREVIEW_HEIGHT = 400;

  // Convert zones from normalized (0-1) to pixels for display
  convertZonesToPixels = (zones: IAnalyticsZone[]): IAnalyticsZone[] => {
    const el = this.previewRef.current;
    const width = el?.getBoundingClientRect().width || AnalyticsProcessorZonesModal.PREVIEW_WIDTH;
    const height = el?.getBoundingClientRect().height || AnalyticsProcessorZonesModal.PREVIEW_HEIGHT;

    return zones.map((zone) => {
      // Check if zone is already in pixels (values > 1) or normalized (0-1)
      const isNormalized = zone.x <= 1 && zone.y <= 1 && zone.w <= 1 && zone.h <= 1;

      if (isNormalized) {
        return {
          ...zone,
          x: Math.round(zone.x * width),
          y: Math.round(zone.y * height),
          w: Math.round(zone.w * width),
          h: Math.round(zone.h * height)
        };
      }
      return {
        ...zone,
        x: Math.round(zone.x),
        y: Math.round(zone.y),
        w: Math.round(zone.w),
        h: Math.round(zone.h)
      };
    });
  };

  // Convert zones from pixels to normalized (0-1) for storage
  convertZonesToNormalized = (zones: IAnalyticsZone[]): IAnalyticsZone[] => {
    const el = this.previewRef.current;
    const width = el?.getBoundingClientRect().width || AnalyticsProcessorZonesModal.PREVIEW_WIDTH;
    const height = el?.getBoundingClientRect().height || AnalyticsProcessorZonesModal.PREVIEW_HEIGHT;

    return zones.map((zone) => ({
      ...zone,
      x: parseFloat((zone.x / width).toFixed(4)),
      y: parseFloat((zone.y / height).toFixed(4)),
      w: parseFloat((zone.w / width).toFixed(4)),
      h: parseFloat((zone.h / height).toFixed(4))
    }));
  };

  handleSave = () => {
    const { zones, eventTypes, previewSampleUrl } = this.state;
    // Convert pixel zones to normalized (0-1) before saving
    const normalizedZones = this.convertZonesToNormalized(zones);

    const updatedConfig: IAnalyticsProcessorConfig = {
      ...this.props.config,
      zones: normalizedZones,
      eventTypes,
      previewSampleUrl
    };
    this.props.onConfigUpdate(updatedConfig);
    this.props.onClose();
  };

  // Zone drawing helpers
  getLocalPoint = (clientX: number, clientY: number) => {
    const el = this.previewRef.current;
    if (!el) return { x: 0, y: 0 };
    const rect = el.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(clientX - rect.left, rect.width)),
      y: Math.max(0, Math.min(clientY - rect.top, rect.height))
    };
  };

  onPreviewMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).dataset.zoneHandle) return;
    const pt = this.getLocalPoint(e.clientX, e.clientY);
    this.setState({
      isDrawing: true,
      startPoint: pt,
      tempZone: { x: pt.x, y: pt.y, w: 0, h: 0 },
      selectedZoneId: null
    });
  };

  onPreviewMouseMove = (e: React.MouseEvent) => {
    const { isDrawing, startPoint } = this.state;
    if (!isDrawing || !startPoint) return;
    const pt = this.getLocalPoint(e.clientX, e.clientY);
    const nx = Math.min(startPoint.x, pt.x);
    const ny = Math.min(startPoint.y, pt.y);
    const nw = Math.abs(pt.x - startPoint.x);
    const nh = Math.abs(pt.y - startPoint.y);
    this.setState({ tempZone: { x: nx, y: ny, w: nw, h: nh } });
  };

  onPreviewMouseUp = () => {
    const { isDrawing, tempZone, zones } = this.state;
    if (!isDrawing) return;

    if (!tempZone || tempZone.w < 5 || tempZone.h < 5) {
      this.setState({ isDrawing: false, tempZone: null, startPoint: null });
      return;
    }

    const newZone: IAnalyticsZone = {
      id: `zone_${Date.now()}`,
      name: `Zone ${zones.length + 1}`,
      x: Math.round(tempZone.x),
      y: Math.round(tempZone.y),
      w: Math.max(1, Math.round(tempZone.w)),
      h: Math.max(1, Math.round(tempZone.h)),
      triggers: []
    };

    this.setState({
      zones: [...zones, newZone],
      isDrawing: false,
      tempZone: null,
      startPoint: null,
      selectedZoneId: newZone.id
    });
  };

  toggleEventType = (eventType: string, checked: boolean) => {
    const { eventTypes, zones } = this.state;
    let newEventTypes: string[];

    if (checked) {
      newEventTypes = [...eventTypes, eventType];
    } else {
      newEventTypes = eventTypes.filter((et) => et !== eventType);
      // Prune zone triggers that are no longer selected
    }

    const prunedZones = zones.map((z) => ({
      ...z,
      triggers: z.triggers.filter((t) => newEventTypes.includes(t))
    }));

    this.setState({ eventTypes: newEventTypes, zones: prunedZones });
  };

  deleteCheckedZones = () => {
    const { zones, checkedZoneIds, selectedZoneId } = this.state;
    if (checkedZoneIds.length === 0) return;

    const updated = zones.filter((z) => !checkedZoneIds.includes(z.id));
    this.setState({
      zones: updated,
      checkedZoneIds: [],
      selectedZoneId: selectedZoneId && checkedZoneIds.includes(selectedZoneId) ? null : selectedZoneId
    });
  };

  toggleCheckZone = (zoneId: string) => {
    this.setState((prev) => ({
      checkedZoneIds: prev.checkedZoneIds.includes(zoneId)
        ? prev.checkedZoneIds.filter((x) => x !== zoneId)
        : [...prev.checkedZoneIds, zoneId]
    }));
  };

  toggleCheckAll = () => {
    const { zones, checkedZoneIds } = this.state;
    const allIds = zones.map((z) => z.id);
    const allSelected = allIds.length > 0 && allIds.every((id) => checkedZoneIds.includes(id));
    this.setState({ checkedZoneIds: allSelected ? [] : allIds });
  };

  toggleCollapse = (zoneId: string) => {
    this.setState((prev) => ({
      collapsedZoneIds: prev.collapsedZoneIds.includes(zoneId)
        ? prev.collapsedZoneIds.filter((x) => x !== zoneId)
        : [...prev.collapsedZoneIds, zoneId]
    }));
  };

  updateZoneById = (zoneId: string, updates: Partial<IAnalyticsZone>) => {
    this.setState((prev) => ({
      zones: prev.zones.map((z) => (z.id === zoneId ? { ...z, ...updates } : z))
    }));
  };

  render(): ReactNode {
    const {
      zones,
      eventTypes,
      previewSampleUrl,
      selectedZoneId,
      checkedZoneIds,
      collapsedZoneIds,
      showEventTypes,
      tempZone
    } = this.state;

    return (
      <KModal
        className="analytics-processor-zones-modal"
        header="Analytics Configuration"
        showModal={this.props.isOpen}
        closeModal={this.props.onClose}
        width={1000}
        footer={<KModalFooter onCancel={this.props.onClose} submitLabel="Save" onSubmit={this.handleSave} />}
      >
        <div className="analytics-zones-modal__container">
          {/* Preview Column - Zone Drawing Area */}
          <div className="analytics-zones-modal__preview-column">
            <div
              ref={this.previewRef}
              className="analytics-zones-modal__preview-area"
              onMouseDown={this.onPreviewMouseDown}
              onMouseMove={this.onPreviewMouseMove}
              onMouseUp={this.onPreviewMouseUp}
            >
              {/* Placeholder preview image */}
              <img
                src={previewSampleUrl || "/placeholder-preview.png"}
                alt="preview"
                className="analytics-zones-modal__preview-image"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
              {/* Render saved zones */}
              {zones.map((z) => (
                <div
                  key={z.id}
                  className={`analytics-zones-modal__zone ${
                    selectedZoneId === z.id ? "analytics-zones-modal__zone--selected" : ""
                  }`}
                  style={{ left: `${z.x}px`, top: `${z.y}px`, width: `${z.w}px`, height: `${z.h}px` }}
                  onClick={(e) => {
                    e.stopPropagation();
                    // Collapse all zones except the clicked one
                    const newCollapsedZoneIds = zones.filter((zone) => zone.id !== z.id).map((zone) => zone.id);

                    this.setState(
                      {
                        selectedZoneId: z.id,
                        collapsedZoneIds: newCollapsedZoneIds
                      },
                      () => {
                        // Scroll to the zone in the list after state update
                        setTimeout(() => {
                          this.zoneRefs[z.id]?.scrollIntoView({ behavior: "smooth", block: "nearest" });
                        }, 100);
                      }
                    );
                  }}
                  data-zone-id={z.id}
                >
                  <div className="analytics-zones-modal__zone-label">{z.name}</div>
                </div>
              ))}
              {/* Render temporary zone while drawing */}
              {tempZone && (
                <div
                  className="analytics-zones-modal__zone-temp"
                  style={{
                    left: `${tempZone.x}px`,
                    top: `${tempZone.y}px`,
                    width: `${tempZone.w}px`,
                    height: `${tempZone.h}px`
                  }}
                />
              )}
            </div>
          </div>

          {/* List Column - Event Types and Zones */}
          <div className="analytics-zones-modal__list-column">
            {/* Event Types Section */}
            <div className="analytics-zones-modal__event-types-section">
              <div className="analytics-zones-modal__collapse-header">
                <span className="analytics-zones-modal__section-label">Event Types</span>
                <KuikaButton
                  type="tertiary"
                  size="sm"
                  isIcon
                  onClick={() => this.setState((prev) => ({ showEventTypes: !prev.showEventTypes }))}
                >
                  <CaretDownOutlined
                    className={`analytics-zones-modal__collapse-icon ${
                      showEventTypes ? "analytics-zones-modal__collapse-icon--expanded" : ""
                    }`}
                  />
                </KuikaButton>
              </div>
              {showEventTypes && (
                <>
                  <div className="analytics-zones-modal__event-add-row">
                    <KuikaSelectbox
                      placeholder="Add event…"
                      size="sm"
                      options={DEFAULT_EVENT_TYPES.filter((et) => !eventTypes.includes(et)).map((et) => ({
                        value: et,
                        label: et.replace(/_/g, " ")
                      }))}
                      onChange={(value) => {
                        if (value) {
                          this.toggleEventType(value as string, true);
                        }
                      }}
                      value={null}
                    />
                  </div>
                  <div className="analytics-zones-modal__event-tags-row">
                    {eventTypes.length === 0 ? (
                      <span className="analytics-zones-modal__empty-hint">No events selected.</span>
                    ) : (
                      eventTypes.map((et) => {
                        const eventLabel: string = et.replace(/_/g, " ");
                        return (
                          <KuikaBadge
                            key={et}
                            content={eventLabel}
                            color="blue"
                            rightIcon={
                              // A real button, so the event can be dropped from the keyboard too.
                              <button
                                type="button"
                                className="analytics-zones-modal__tag-remove"
                                aria-label={`Remove ${eventLabel}`}
                                onClick={(e: React.MouseEvent<HTMLButtonElement>): void => {
                                  e.stopPropagation();
                                  this.toggleEventType(et, false);
                                }}
                              >
                                ×
                              </button>
                            }
                          />
                        );
                      })
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Zones List Header */}
            <div className="analytics-zones-modal__list-header">
              <KuikaCheckbox
                label="Select all"
                checked={zones.length > 0 && zones.every((z) => checkedZoneIds.includes(z.id))}
                onChange={() => this.toggleCheckAll()}
              />
              <KuikaButton
                type="tertiary"
                color="danger"
                size="sm"
                isIcon
                onClick={() => this.deleteCheckedZones()}
                disabled={checkedZoneIds.length === 0}
              >
                <KuikaSvg
                  type="common"
                  name="trash"
                  color={checkedZoneIds.length === 0 ? "var(--red-3)" : "var(--red-5)"}
                />
              </KuikaButton>
            </div>

            {/* Zones List */}
            <div className="analytics-zones-modal__zone-list">
              {zones.length === 0 ? (
                <span className="analytics-zones-modal__empty-hint">
                  Draw zones on the preview area by clicking and dragging.
                </span>
              ) : (
                zones.map((z) => {
                  const collapsed = collapsedZoneIds.includes(z.id);
                  const checked = checkedZoneIds.includes(z.id);
                  return (
                    <div
                      key={z.id}
                      className="analytics-zones-modal__zone-list-item"
                      ref={(el) => (this.zoneRefs[z.id] = el)}
                    >
                      <div className="analytics-zones-modal__zone-list-row">
                        <KuikaCheckbox checked={checked} onChange={() => this.toggleCheckZone(z.id)} />
                        <button
                          className="analytics-zones-modal__zone-list-title"
                          onClick={() => {
                            this.toggleCollapse(z.id);
                            this.setState({ selectedZoneId: z.id });
                          }}
                        >
                          {z.name}
                        </button>
                      </div>
                      {!collapsed && (
                        <div className="analytics-zones-modal__zone-list-details">
                          <div className="analytics-zones-modal__zone-dims">
                            <div className="analytics-zones-modal__zone-field analytics-zones-modal__zone-field--full">
                              <span className="analytics-zones-modal__field-label">Name</span>
                              <KuikaInput
                                size="sm"
                                value={z.name || ""}
                                onChange={(e, val) => this.updateZoneById(z.id, { name: String(val) })}
                              />
                            </div>
                            <div className="analytics-zones-modal__zone-field">
                              <span className="analytics-zones-modal__field-label">X</span>
                              <KuikaInput
                                size="sm"
                                type="number"
                                value={z.x}
                                onChange={(e, val) => this.updateZoneById(z.id, { x: Number(val) || 0 })}
                              />
                            </div>
                            <div className="analytics-zones-modal__zone-field">
                              <span className="analytics-zones-modal__field-label">Y</span>
                              <KuikaInput
                                size="sm"
                                type="number"
                                value={z.y}
                                onChange={(e, val) => this.updateZoneById(z.id, { y: Number(val) || 0 })}
                              />
                            </div>
                            <div className="analytics-zones-modal__zone-field">
                              <span className="analytics-zones-modal__field-label">W</span>
                              <KuikaInput
                                size="sm"
                                type="number"
                                value={z.w}
                                onChange={(e, val) => this.updateZoneById(z.id, { w: Number(val) || 1 })}
                              />
                            </div>
                            <div className="analytics-zones-modal__zone-field">
                              <span className="analytics-zones-modal__field-label">H</span>
                              <KuikaInput
                                size="sm"
                                type="number"
                                value={z.h}
                                onChange={(e, val) => this.updateZoneById(z.id, { h: Number(val) || 1 })}
                              />
                            </div>
                          </div>

                          {/* Event Triggers for Zone */}
                          <div className="analytics-zones-modal__triggers-section">
                            <span className="analytics-zones-modal__field-label">Event Triggers</span>
                            <div className="analytics-zones-modal__triggers-list">
                              {eventTypes.length === 0 ? (
                                <span className="analytics-zones-modal__empty-hint">Add event types first.</span>
                              ) : (
                                eventTypes.map((eventType) => (
                                  <KuikaCheckbox
                                    key={eventType}
                                    label={eventType.replace(/_/g, " ")}
                                    checked={(z.triggers || []).includes(eventType)}
                                    onChange={(e) => {
                                      const currentTriggers = z.triggers || [];
                                      const newTriggers = e.target.checked
                                        ? [...currentTriggers, eventType]
                                        : currentTriggers.filter((t) => t !== eventType);
                                      this.updateZoneById(z.id, { triggers: newTriggers });
                                    }}
                                  />
                                ))
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </KModal>
    );
  }
}
